import { ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAuth0, AuthHttpInterceptor } from '@auth0/auth0-angular';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import { routes } from './app.routes';
import { SessionExpiredInterceptor } from '@formrails/shared/data-access';
import { environment } from '../environments/environment';

/**
 * PrimeNG theme preset for formrails. Based on Aura, with the `primary` palette anchored
 * to the brand navy (#0b3147) so PrimeNG controls match the app palette.
 * Form-builder takes little of it: a `<p-toast>` and the preview's tabs.
 */
const OsstrePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#e7eef2',
      100: '#c3d4dd',
      200: '#9bb8c6',
      300: '#6f99ad',
      400: '#3f7790',
      500: '#0b3147',
      600: '#0b2b3d',
      700: '#0a2434',
      800: '#081d2a',
      900: '#06151f',
      950: '#030c12',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Configure Auth0 first
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      cacheLocation: 'localstorage', // Persist tokens across page refreshes
      authorizationParams: environment.auth0.authorizationParams,
      httpInterceptor: environment.httpInterceptor
    }),
    // Explicitly register the HTTP interceptors (required for standalone apps)
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true
    },
    // A 401 on an authenticated request means the session has expired: clear
    // local data and send the user to login. Must follow AuthHttpInterceptor.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionExpiredInterceptor,
      multi: true
    },
    // Configure HttpClient with DI interceptors support (must come after provideAuth0)
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes),
    // App-wide PrimeNG theme (styled mode). Light only.
    providePrimeNG({ theme: { preset: OsstrePreset, options: { darkModeSelector: false } } }),
    // Root MessageService backs the global <p-toast> in AppComponent; the user/role
    // signal stores inject it for success/error notifications.
    MessageService
    // Form builder state is managed by the `FormStore` signal store
    // (`providedIn: 'root'`), so no global store/effects registration is required.
  ]
};
